import React, { useEffect, useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';

interface BallDbRecord {
  id: string;
  brand: string;
  name: string;
  coverstock: string | null;
  core: string | null;
  imageUrl: string | null;
  source: string;
  releasedAt: string | null;
}

export interface BallPickerSelection {
  ballDbId: string;
  brand: string;
  name: string;
  coverstock: string;
  core: string;
  imageUrl: string | null;
}

interface BallPickerProps {
  open: boolean;
  onClose: () => void;
  onSelect: (ball: BallPickerSelection) => void;
}

// Module-level cache: the 376KB json chunk is fetched once per session,
// no matter how many times the picker opens or how many pages mount it.
let ballsCache: BallDbRecord[] | null = null;

const MAX_RESULTS = 60;

const inputCls =
  'w-full px-4 py-3 border border-tiger-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-tiger-primary-black focus:border-transparent transition';

export const BallPicker: React.FC<BallPickerProps> = ({ open, onClose, onSelect }) => {
  const [balls, setBalls] = useState<BallDbRecord[] | null>(ballsCache);
  const [loadError, setLoadError] = useState(false);
  const [search, setSearch] = useState('');
  const [brand, setBrand] = useState('');

  // Lazy-load the database the first time the picker opens
  useEffect(() => {
    if (!open) return;
    if (ballsCache) {
      setBalls(ballsCache);
      return;
    }
    let cancelled = false;
    // Lazy dynamic import keeps the 376KB database out of the main bundle.
    import('@/data/balls.json')
      .then((mod: { default: { generatedAt: string; balls: BallDbRecord[] } }) => {
        ballsCache = mod.default.balls;
        if (!cancelled) setBalls(ballsCache);
      })
      .catch((err) => {
        console.error('Failed to load ball database:', err);
        if (!cancelled) setLoadError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const brands = useMemo(() => {
    if (!balls) return [];
    return Array.from(new Set(balls.map((b) => b.brand))).sort((a, b) => a.localeCompare(b));
  }, [balls]);

  const matches = useMemo(() => {
    if (!balls) return [];
    const q = search.trim().toLowerCase();
    return balls.filter(
      (b) => (!brand || b.brand === brand) && (!q || b.name.toLowerCase().includes(q))
    );
  }, [balls, search, brand]);

  if (!open) return null;

  const shown = matches.slice(0, MAX_RESULTS);

  const pick = (b: BallDbRecord) => {
    onSelect({
      ballDbId: b.id,
      brand: b.brand,
      name: b.name,
      coverstock: b.coverstock ?? '',
      core: b.core ?? '',
      imageUrl: b.imageUrl,
    });
    onClose();
  };

  const chipCls = (active: boolean) =>
    `px-3 py-1 rounded-full text-xs font-bold border transition-colors ${
      active
        ? 'bg-tiger-primary-black text-white border-tiger-primary-black'
        : 'bg-white text-tiger-neutral-600 border-tiger-neutral-300 hover:border-tiger-primary-black hover:text-tiger-primary-black'
    }`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" onClick={onClose} aria-hidden="true" />

      {/* Panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search ball database"
        className="relative bg-white rounded-2xl shadow-tiger-xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="p-5 pb-4 border-b border-tiger-neutral-200 space-y-3 shrink-0">
          <div className="flex items-center justify-between gap-3">
            <h2 className="font-heading text-2xl tracking-wide text-tiger-primary-black uppercase">
              Ball Database
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-lg text-tiger-neutral-500 hover:text-tiger-primary-black hover:bg-tiger-neutral-100 transition-colors"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-tiger-neutral-400 pointer-events-none" />
            <input
              autoFocus
              className={`${inputCls} pl-10`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by ball name…"
            />
          </div>
          {brands.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              <button type="button" className={chipCls(brand === '')} onClick={() => setBrand('')}>
                All
              </button>
              {brands.map((br) => (
                <button key={br} type="button" className={chipCls(brand === br)} onClick={() => setBrand(br)}>
                  {br}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Results */}
        <div className="p-5 overflow-y-auto">
          {loadError ? (
            <p className="text-center text-sm text-red-600 py-10">
              Couldn't load the ball database. Check your connection and try again.
            </p>
          ) : !balls ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-tiger-primary-black" />
            </div>
          ) : shown.length === 0 ? (
            <p className="text-center text-sm text-tiger-neutral-500 py-10">
              No balls match. Try a different name or brand.
            </p>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {shown.map((b) => (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => pick(b)}
                    className="text-left border border-tiger-neutral-200 rounded-xl p-3 hover:border-tiger-primary-black hover:shadow-tiger transition-all"
                  >
                    <div className="aspect-square bg-tiger-neutral-50 rounded mb-2 flex items-center justify-center overflow-hidden">
                      {b.imageUrl ? (
                        <img
                          src={b.imageUrl}
                          alt={`${b.brand} ${b.name}`}
                          loading="lazy"
                          className="w-full h-full aspect-square object-contain"
                        />
                      ) : (
                        <div className="w-3/4 aspect-square rounded-full bg-tiger-primary-black flex items-center justify-center p-2">
                          <span className="text-white text-[10px] font-bold uppercase tracking-widest text-center leading-tight">
                            {b.brand}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="font-bold text-sm text-tiger-primary-black leading-tight">{b.name}</div>
                    <div className="text-xs text-tiger-neutral-500 mt-0.5">
                      {b.brand}
                      {b.coverstock ? ` · ${b.coverstock}` : ''}
                    </div>
                  </button>
                ))}
              </div>
              {matches.length > MAX_RESULTS && (
                <p className="text-center text-xs text-tiger-neutral-500 mt-4">
                  Showing {MAX_RESULTS} of {matches.length} matches, refine your search to see the rest.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
